/**
 * @coeldery/camera-capture —— **App 內相機**（影相 → canvas JPEG，多尺度壓縮）
 *
 * 為何要 in-app（唔淨用 `<input capture>`）：
 *   系統相機會把相片**留喺手機相簿**（單據副本散落裝置），而且 canvas 出嘅 JPEG **冇 EXIF**
 *   （GPS 由根本唔會上載）。財務單據係敏感資料 → 呢兩點都要。
 *
 * 對照來源：CIRMS `src/components/CameraCapture.tsx`（2026-09-20 磚 ⑦k；`decisions/002` R3 抽出）
 *
 * 紅線
 *   · 離開頁面／切走 → 一定要停**所有** track（唔准留低開住鏡頭）
 *   · 觀景器尺寸由**真實影片尺寸**決定（唔准硬套 4:3／16:9）
 *   · 冇鏡頭／拒權限 → 退回「揀圖」（同一條上載路）
 *   · 相片**唔入相簿**（只喺記憶體 → 即上載）
 *
 * 2026-10-06：加 `applyFocus`（連續／tap-to-focus；能力探測）＋`coverRegion`（WYSIWYG 裁剪區塊）
 *   ＋ `captureJpeg` optional `region`。見 `.coappery/modules/manifest.json` localChanges（待回流入庫）。
 */

export type FacingMode = 'environment' | 'user';

export interface FrameSize { width: number; height: number }

export interface StreamLike {
  getTracks(): { stop(): void }[];
}

export interface CameraDeps {
  /** 注入（測試用假鏡頭；正式 = `navigator.mediaDevices.getUserMedia`）*/
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<unknown>;
}

/** 由影片元素真實尺寸決定觀景器比例（0 → 未 ready）*/
export function frameSize(width: number, height: number): FrameSize | null {
  if (!width || !height) return null;
  return { width, height };
}

export function nextFacingMode(current: FacingMode): FacingMode {
  return current === 'environment' ? 'user' : 'environment';
}

/** 有冇相機可用（避免 iPad／桌面 Safari 直接爆）*/
export function cameraAvailable(nav: Navigator | undefined): boolean {
  return Boolean(nav?.mediaDevices?.getUserMedia);
}

/**
 * 開鏡頭。⚠️ **一定要要求高解像度**：唔指定 width／height 好多手機只俾 **640×480** → 影出嚟矇；
 * `opts.width/height` 係 `ideal`（瀏覽器會揀最接近嘅）；亦請求**連續自動對焦**。
 */
export async function openCamera(
  mode: FacingMode,
  deps: CameraDeps,
  opts: { width?: number; height?: number } = {},
): Promise<StreamLike> {
  const width = opts.width ?? 2560;
  const height = opts.height ?? 1440;
  const stream = (await deps.getUserMedia({
    video: {
      facingMode: { ideal: mode },
      width: { ideal: width },
      height: { ideal: height },
      // ⚠️ `focusMode` 未喺 TS DOM lib 嘅 MediaTrackConstraintSet → 要 cast（瀏覽器支援嘅先生效）
      advanced: [{ focusMode: 'continuous' } as unknown as MediaTrackConstraintSet],
    },
    audio: false,
  })) as StreamLike;
  return stream;
}

/** 開完之後**再嘗試拉高解像度**（部分瀏覽器第一次只俾低解像度；唔支援就靜默回 false）*/
export async function applyResolution(stream: StreamLike, width: number, height: number): Promise<boolean> {
  const s = stream as unknown as { getVideoTracks?: () => { applyConstraints?: (c: unknown) => Promise<void> }[] };
  const track = s.getVideoTracks?.()[0];
  if (!track?.applyConstraints) return false;
  try { await track.applyConstraints({ width: { ideal: width }, height: { ideal: height } }); return true; } catch { return false; }
}

export function stopStream(stream: StreamLike | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

/** 影片 track（若可用）—— 用嚟設定對焦等能力 */
type VideoTrackLike = {
  applyConstraints?: (c: unknown) => Promise<void>;
  getCapabilities?: () => Record<string, unknown>;
};

function firstVideoTrack(stream: StreamLike): VideoTrackLike | null {
  const s = stream as unknown as { getVideoTracks?: () => VideoTrackLike[] };
  return s.getVideoTracks?.()[0] ?? null;
}

/**
 * 套用對焦（**能力探測式**：唔支援就靜默回 false，唔阻影相）。
 *  · `continuous`                 ＝ 持續自動對焦（文件會自己追焦）
 *  · `single-shot` ＋ `points`    ＝ 鎖定某一點（tap-to-focus，「鎖住」嗰樣嘢）
 *  ⚠️ `focusMode`／`pointsOfInterest` 支援度視乎裝置瀏覽器（好多手機唔支援 → no-op，屬正常）。
 */
export async function applyFocus(
  stream: StreamLike,
  opts: { mode?: 'continuous' | 'single-shot' | 'manual'; points?: { x: number; y: number }[] } = {},
): Promise<boolean> {
  const track = firstVideoTrack(stream);
  if (!track?.applyConstraints) return false;
  const caps = track.getCapabilities?.() ?? {};
  const advanced: Record<string, unknown> = {};
  const mode = opts.points ? (opts.mode ?? 'single-shot') : opts.mode;
  if (mode && 'focusMode' in caps) advanced.focusMode = mode;
  if (opts.points && 'pointsOfInterest' in caps) advanced.pointsOfInterest = opts.points;
  if (Object.keys(advanced).length === 0) return false;
  try { await track.applyConstraints({ advanced: [advanced] }); return true; } catch { return false; }
}

/**
 * `object-fit: cover` 之下，畫面上**實際見到**嘅來源區塊（WYSIWYG 裁剪用）。
 * 回傳**影片座標系**嘅矩形（sx, sy, sw, sh）；`display` 尺寸為 0 → 回整格。
 */
export function coverRegion(
  video: { width: number; height: number },
  display: { width: number; height: number },
): { sx: number; sy: number; sw: number; sh: number } {
  if (!display.width || !display.height) return { sx: 0, sy: 0, sw: video.width, sh: video.height };
  const vAR = video.width / video.height;
  const dAR = display.width / display.height;
  if (vAR > dAR) {
    const sw = video.height * dAR;
    return { sx: (video.width - sw) / 2, sy: 0, sw, sh: video.height };
  }
  const sh = video.width / dAR;
  return { sx: 0, sy: (video.height - sh) / 2, sw: video.width, sh };
}

/** 影相 → 壓縮 JPEG（長邊 ≤ `maxEdge`；`quality` 0.6–0.9）。可傳 `region` 做 WYSIWYG 裁剪 */
export function captureJpeg(
  video: HTMLVideoElement,
  opts: { maxEdge?: number; quality?: number; region?: { sx: number; sy: number; sw: number; sh: number } } = {},
): { blob: Promise<Blob | null>; width: number; height: number } {
  const maxEdge = opts.maxEdge ?? 1600;
  const quality = opts.quality ?? 0.8;
  const sx = opts.region?.sx ?? 0;
  const sy = opts.region?.sy ?? 0;
  const sw = opts.region?.sw ?? video.videoWidth;
  const sh = opts.region?.sh ?? video.videoHeight;
  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const width = Math.round(sw * scale);
  const height = Math.round(sh * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { blob: Promise.resolve(null), width, height };
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, width, height);
  // ⚠️ canvas → JPEG 冇 EXIF（GPS 唔會被帶上載）
  const blob = new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', quality));
  return { blob, width, height };
}

/** 由圖片檔案（揀圖路徑）再壓縮一次（同一條上載路都要剝 EXIF）*/
export async function downscaleFile(file: File | Blob, maxEdge = 1600): Promise<{ blob: Blob; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.8));
  return { blob: blob ?? (file as Blob), width, height };
}
